import { CalendarIcon } from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface DateFieldOptions {
  dateFormat: "local" | "friendly" | "us" | "european" | "iso";
  includeTime: boolean;
  timeFormat: "12h" | "24h";
  useSharedTimezone: boolean;
  timezone: string;
  displayTimezone: boolean;
  defaultToCurrentDate: boolean;
}

export const DEFAULT_DATE_OPTIONS: DateFieldOptions = {
  dateFormat: "local",
  includeTime: false,
  timeFormat: "24h",
  useSharedTimezone: false,
  timezone: "GMT/UTC",
  displayTimezone: false,
  defaultToCurrentDate: false,
};

// Generate 30-minute time slots
const TIME_SLOTS_24H: string[] = [];
for (let h = 0; h < 24; h++) {
  for (const m of [0, 30]) {
    TIME_SLOTS_24H.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
  }
}

function formatTime12h(time24: string): string {
  const [hStr, mStr] = time24.split(":");
  let h = Number.parseInt(hStr, 10);
  const ampm = h >= 12 ? "pm" : "am";
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${mStr}${ampm}`;
}

function getTimezoneAbbr(timezone: string, date: Date): string {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone === "GMT/UTC" ? "UTC" : timezone,
      timeZoneName: "short",
    }).formatToParts(date);
    const tzPart = parts.find((p) => p.type === "timeZoneName");
    return tzPart?.value ?? "";
  } catch {
    return "";
  }
}

export function formatDateValue(value: unknown, options?: DateFieldOptions): string {
  if (value === null || value === undefined || value === "") return "";
  const strVal = String(value);
  const date = new Date(strVal);
  if (Number.isNaN(date.getTime())) return strVal;

  const opts = options ?? DEFAULT_DATE_OPTIONS;

  // Extract date parts in the appropriate timezone
  if (opts.useSharedTimezone && opts.timezone) {
    // For timezone-aware display, we format using Intl
    const tz = opts.timezone === "GMT/UTC" ? "UTC" : opts.timezone;
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).formatToParts(date);
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
    const year = get("year");
    const month = get("month");
    const day = get("day");
    const hour = get("hour");
    const minute = get("minute");

    const datePart = formatDateParts(opts.dateFormat, year, month, day);
    if (!opts.includeTime) {
      return datePart;
    }
    const timePart =
      opts.timeFormat === "24h" ? `${hour}:${minute}` : formatTime12h(`${hour}:${minute}`);
    const tzAbbr = opts.displayTimezone ? ` ${getTimezoneAbbr(tz, date)}` : "";
    return `${datePart}  ${timePart}${tzAbbr}`;
  }

  const year = String(date.getFullYear());
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  const datePart = formatDateParts(opts.dateFormat, year, month, day);
  if (!opts.includeTime) {
    return datePart;
  }
  const hour = String(date.getHours()).padStart(2, "0");
  const minute = String(date.getMinutes()).padStart(2, "0");
  const timePart =
    opts.timeFormat === "24h" ? `${hour}:${minute}` : formatTime12h(`${hour}:${minute}`);
  const tzAbbr = opts.displayTimezone
    ? ` ${getTimezoneAbbr(Intl.DateTimeFormat().resolvedOptions().timeZone, date)}`
    : "";
  return `${datePart}  ${timePart}${tzAbbr}`;
}

function formatDateParts(format: string, year: string, month: string, day: string): string {
  const m = Number.parseInt(month, 10);
  const d = Number.parseInt(day, 10);
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  switch (format) {
    case "friendly":
      return `${d} ${monthNames[m - 1]} ${year}`;
    case "us":
      return `${m}/${d}/${year}`;
    case "european":
      return `${d}/${m}/${year}`;
    case "iso":
      return `${year}-${month}-${day}`;
    default:
      return new Date(`${year}-${month}-${day}T00:00:00`).toLocaleDateString();
  }
}

interface DateFieldEditorProps {
  value: string;
  options?: DateFieldOptions;
  onCommit: (isoValue: string) => void;
  onCancel: () => void;
}

export function DateFieldEditor({ value, options, onCommit, onCancel }: DateFieldEditorProps) {
  const opts = options ?? DEFAULT_DATE_OPTIONS;

  // Parse the initial value
  const initialDate = useMemo(() => {
    if (!value) return undefined;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? undefined : d;
  }, [value]);

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(initialDate);
  const [selectedTime, setSelectedTime] = useState<string>(() => {
    if (!initialDate) return "12:00";
    const h = String(initialDate.getHours()).padStart(2, "0");
    const m = String(initialDate.getMinutes()).padStart(2, "0");
    return `${h}:${m}`;
  });
  const [calendarOpen, setCalendarOpen] = useState(true);
  const [timeDropdownOpen, setTimeDropdownOpen] = useState(false);
  const timeListRef = useRef<HTMLDivElement>(null);

  const buildISOValue = useCallback(
    (date: Date | undefined, time: string): string => {
      if (!date) return "";
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      if (!opts.includeTime) {
        return `${year}-${month}-${day}`;
      }
      return `${year}-${month}-${day}T${time}:00`;
    },
    [opts.includeTime],
  );

  const handleDateSelect = useCallback(
    (day: Date | undefined) => {
      setSelectedDate(day);
      if (day && !opts.includeTime) {
        // Auto-commit for date-only fields
        const iso = buildISOValue(day, selectedTime);
        onCommit(iso);
      }
    },
    [opts.includeTime, buildISOValue, selectedTime, onCommit],
  );

  const handleTimeSelect = useCallback(
    (time: string) => {
      setSelectedTime(time);
      setTimeDropdownOpen(false);
      if (selectedDate) {
        const iso = buildISOValue(selectedDate, time);
        onCommit(iso);
      }
    },
    [selectedDate, buildISOValue, onCommit],
  );

  const displayValue = useMemo(() => {
    if (!selectedDate) return "";
    const iso = buildISOValue(selectedDate, selectedTime);
    return formatDateValue(iso, opts);
  }, [selectedDate, selectedTime, opts, buildISOValue]);

  return (
    <div className="relative">
      <Popover
        open={calendarOpen}
        onOpenChange={(open) => {
          setCalendarOpen(open);
          if (!open) {
            if (selectedDate) {
              onCommit(buildISOValue(selectedDate, selectedTime));
            } else {
              onCancel();
            }
          }
        }}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "flex items-center gap-1 text-left text-xs h-8 w-full px-1",
              "bg-gray-700 border border-gray-600 rounded text-white",
              !selectedDate && "text-gray-400",
            )}
          >
            <CalendarIcon className="h-3 w-3 shrink-0" />
            <span className="truncate">{displayValue || "Pick a date"}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto p-0 bg-gray-800 border-gray-700"
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={handleDateSelect}
            defaultMonth={selectedDate}
            className="text-white [&_.rdp-day]:text-white [&_.rdp-head_cell]:text-gray-400 [&_.rdp-caption_label]:text-white [&_.rdp-nav_button]:text-white [&_.rdp-nav_button]:border-gray-600 [&_.rdp-day_today]:bg-gray-700 [&_.rdp-day_selected]:bg-blue-600 [&_.rdp-day_selected]:text-white"
          />
          {opts.includeTime && (
            <div className="border-t border-gray-700 p-3">
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setTimeDropdownOpen(!timeDropdownOpen)}
                  className="w-full flex items-center justify-between px-3 py-1.5 text-xs bg-gray-700 border border-gray-600 rounded text-white"
                >
                  <span>
                    {opts.timeFormat === "24h" ? selectedTime : formatTime12h(selectedTime)}
                  </span>
                  <svg className="h-3 w-3" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                    <path d="M3 5l3 3 3-3" stroke="currentColor" strokeWidth="1.5" />
                  </svg>
                </button>
                {timeDropdownOpen && (
                  <div
                    ref={timeListRef}
                    className="absolute bottom-full mb-1 left-0 right-0 max-h-48 overflow-y-auto bg-gray-800 border border-gray-600 rounded shadow-lg z-50"
                  >
                    {TIME_SLOTS_24H.map((slot) => (
                      <button
                        key={slot}
                        type="button"
                        onClick={() => handleTimeSelect(slot)}
                        className={cn(
                          "w-full text-left px-3 py-1.5 text-xs hover:bg-gray-700 text-white",
                          slot === selectedTime && "bg-gray-700 font-medium",
                        )}
                      >
                        {opts.timeFormat === "24h" ? slot : formatTime12h(slot)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
